import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import { Eye, EyeOff, Mail, Lock, User, ArrowRight, Phone, MessageSquare, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { Logo } from '../components/Logo';
import { trackLogin, trackSignUp, trackButtonClick, setUserProperties } from '../lib/analytics';

const LoginPage = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '', confirmPassword: '', name: '' });
  const [passwordError, setPasswordError] = useState('');

  const { login, register, loginWithGoogle, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (authLoading || loading) return;
    if (user) {
      if (user.role === 'admin') navigate('/admin');
      else if (user.company_id) navigate(`/portal/${user.company_id}`);
      else navigate('/onboarding');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading]);

  const handleSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const userData = await login(formData.email, formData.password);
      trackLogin('email');
      setUserProperties(userData.user_id, userData.role, userData.company_id);
      toast.success('Welcome back!');
      if (userData.role === 'admin') navigate('/admin');
      else if (userData.company_id) navigate(`/portal/${userData.company_id}`);
      else navigate('/onboarding');
    } catch (error) { toast.error(error.message || 'Authentication failed'); }
    finally { setLoading(false); }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) { setPasswordError('Passwords do not match'); return; }
    setPasswordError('');
    setLoading(true);
    try {
      await register(formData.name, formData.email, formData.password);
      trackSignUp('email');
      toast.success("Account created! Let's set up your AI receptionist.");
      navigate('/onboarding');
    } catch (error) { toast.error(error.message || 'Registration failed'); }
    finally { setLoading(false); }
  };

  const handleGoogleLogin = () => {
    trackButtonClick('google_login', 'login_page');
    sessionStorage.setItem('auth_intent', 'signup');
    loginWithGoogle();
  };

  /* Mouse glow local to left panel */
  const [glow, setGlow] = useState({ x: -200, y: -200 });
  useEffect(() => {
    const h = (e) => setGlow({ x: e.clientX, y: e.clientY });
    window.addEventListener('mousemove', h);
    return () => window.removeEventListener('mousemove', h);
  }, []);

  /* Staggered pill reveal */
  const [pillsVisible, setPillsVisible] = useState(false);
  useEffect(() => { const t = setTimeout(() => setPillsVisible(true), 600); return () => clearTimeout(t); }, []);

  /* Circuit path progress */
  const [cp, setCp] = useState(0);
  useEffect(() => {
    let raf;
    const start = Date.now();
    const tick = () => { setCp(Math.min(1, (Date.now() - start) / 3000)); if (cp < 1) raf = requestAnimationFrame(tick); };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const L = 1200;

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* ── Page-level grain overlay ── */}
      <div className="fixed inset-0 pointer-events-none z-[2] opacity-[0.03] mix-blend-overlay"
        style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")` }} />

      {/* ═══ Left Panel — Mint (like landing page values section) ═══ */}
      <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden bg-[#D4F7DC]">
        {/* Mouse glow */}
        <div className="absolute inset-0 pointer-events-none z-[1]"
          style={{ background: `radial-gradient(600px circle at ${glow.x}px ${glow.y}px, rgba(0,255,209,0.1), transparent 60%)` }} />

        {/* Circuit paths SVG */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 800 900" preserveAspectRatio="none">
          <path d="M-20,200 Q200,100 400,350 T800,280" fill="none" stroke="rgba(0,255,209,0.06)" strokeWidth="1" />
          <path d="M-20,200 Q200,100 400,350 T800,280" fill="none" stroke="#00FFD1" strokeWidth="1.5" strokeLinecap="round"
            style={{ strokeDasharray: L, strokeDashoffset: L - cp * L, opacity: 0.2 }} />
          <path d="M820,650 Q600,700 400,500 T-20,600" fill="none" stroke="rgba(196,149,106,0.05)" strokeWidth="1" />
          <path d="M820,650 Q600,700 400,500 T-20,600" fill="none" stroke="#C4956A" strokeWidth="1.5" strokeLinecap="round"
            style={{ strokeDasharray: L, strokeDashoffset: L - cp * L * 0.7, opacity: 0.15 }} />
          <circle cx={150 + cp * 500} cy={250 + Math.sin(cp * 3.14) * 80} r="3" fill="#00FFD1" opacity={cp > 0.1 ? 0.5 : 0}>
            <animate attributeName="r" values="2;5;2" dur="2s" repeatCount="indefinite" />
          </circle>
          <circle cx={650 - cp * 400} cy={550 - Math.sin(cp * 3.14) * 60} r="3" fill="#C4956A" opacity={cp > 0.2 ? 0.4 : 0}>
            <animate attributeName="r" values="2;4;2" dur="2.5s" repeatCount="indefinite" />
          </circle>
        </svg>

        {/* Grid pattern */}
        <div className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: `repeating-linear-gradient(0deg, transparent, transparent 1px, rgba(42,42,42,0.1) 1px, rgba(42,42,42,0.1) 2px),
              repeating-linear-gradient(-90deg, transparent, transparent 1px, rgba(42,42,42,0.1) 1px, rgba(42,42,42,0.1) 2px)`,
            backgroundSize: '60px 60px',
          }} />

        <div className="relative z-10 flex flex-col justify-center px-12 lg:px-16 xl:px-20 w-full">
          <div className="space-y-8 max-w-lg">
            {/* Logo */}
            <Link to="/" className="inline-block" data-testid="login-logo-link">
              <Logo height={40} dark />
            </Link>

            {/* Heading with char reveal effect */}
            <div>
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#C4956A] mb-3">Welcome Back</p>
              <h1 className="text-4xl lg:text-5xl font-semibold text-[#2A2A2A] leading-[1.1]" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Your AI
                <br />
                <span className="text-[#00FFD1] drop-shadow-[0_0_20px_rgba(0,255,209,0.3)]">Dashboard</span>
                <br />
                Awaits
              </h1>
            </div>

            <p className="text-base text-[#2A2A2A]/50 max-w-sm leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>
              Track your AI receptionist performance, manage leads, and grow your business with intelligent insights.
            </p>

            {/* Feature pills — staggered reveal */}
            <div className="flex flex-wrap gap-3 pt-2">
              {[
                { icon: Phone, label: 'Call Analytics', accent: '#00FFD1' },
                { icon: MessageSquare, label: 'Lead Management', accent: '#C4956A' },
                { icon: Calendar, label: 'Booking Insights', accent: '#7DFFD4' },
              ].map((pill, i) => {
                const Icon = pill.icon;
                return (
                  <div key={pill.label}
                    className="flex items-center gap-2 px-4 py-2 rounded-full border transition-all duration-500"
                    style={{
                      backgroundColor: `${pill.accent}10`,
                      borderColor: `${pill.accent}25`,
                      opacity: pillsVisible ? 1 : 0,
                      transform: pillsVisible ? 'translateY(0)' : 'translateY(16px)',
                      transitionDelay: `${i * 150}ms`,
                    }}>
                    <Icon className="h-3.5 w-3.5" style={{ color: pill.accent }} />
                    <span className="text-sm text-[#2A2A2A]/60" style={{ fontFamily: "'Manrope', sans-serif" }}>{pill.label}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Bottom decoration */}
          <div className="absolute bottom-8 left-12 lg:left-16 xl:left-20 right-12 lg:right-16 xl:right-20">
            <div className="h-px bg-gradient-to-r from-transparent via-[#00FFD1]/30 to-transparent" />
            <p className="text-center text-xs mt-4 text-[#2A2A2A]/30" style={{ fontFamily: "'Manrope', sans-serif" }}>
              Never miss a call. Never miss a booking.
            </p>
          </div>
        </div>
      </div>

      {/* ═══ Right Panel — Dark auth form ═══ */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12 bg-[#111] relative overflow-hidden">
        {/* Subtle corner glow */}
        <div className="absolute top-0 right-0 w-[300px] h-[300px] bg-[#00FFD1]/[0.03] rounded-bl-full pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[200px] h-[200px] bg-[#C4956A]/[0.02] rounded-tr-full pointer-events-none" />

        <div className="w-full max-w-md space-y-7 relative z-10">
          {/* Mobile Logo */}
          <div className="flex lg:hidden items-center justify-center mb-6">
            <Link to="/" data-testid="login-mobile-logo-link">
              <Logo height={32} />
            </Link>
          </div>

          {/* Auth Tabs */}
          <Tabs defaultValue="signin" className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-[#1e1e1e] border border-[#F5F0E0]/5 rounded-full p-1 h-auto">
              <TabsTrigger
                value="signin"
                onClick={() => setPasswordError('')}
                className="data-[state=active]:bg-[#00FFD1] data-[state=active]:text-[#111] data-[state=active]:shadow-[0_0_15px_rgba(0,255,209,0.2)] rounded-full py-2 text-[#F5F0E0]/50 text-sm font-semibold transition-all"
                style={{ fontFamily: "'Manrope', sans-serif" }}
              >
                Sign In
              </TabsTrigger>
              <TabsTrigger
                value="signup"
                onClick={() => setPasswordError('')}
                className="data-[state=active]:bg-[#00FFD1] data-[state=active]:text-[#111] data-[state=active]:shadow-[0_0_15px_rgba(0,255,209,0.2)] rounded-full py-2 text-[#F5F0E0]/50 text-sm font-semibold transition-all"
                style={{ fontFamily: "'Manrope', sans-serif" }}
              >
                Sign Up
              </TabsTrigger>
            </TabsList>

            {/* Sign In */}
            <TabsContent value="signin" className="mt-7">
              <form onSubmit={handleSignIn} className="space-y-5">
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-[#F5F0E0]/40 uppercase tracking-wider" style={{ fontFamily: "'Manrope', sans-serif" }}>Email</label>
                  <div className="relative group">
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#F5F0E0]/20 group-focus-within:text-[#00FFD1] transition-colors" />
                    <Input type="email" placeholder="you@company.com" value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="pl-10 bg-[#1e1e1e] border-[#F5F0E0]/5 text-[#F5F0E0] placeholder:text-[#F5F0E0]/20 focus:border-[#00FFD1]/40 focus:ring-[#00FFD1]/10 focus:shadow-[0_0_12px_rgba(0,255,209,0.05)] rounded-xl h-11 transition-all"
                      required data-testid="email-input" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-[#F5F0E0]/40 uppercase tracking-wider" style={{ fontFamily: "'Manrope', sans-serif" }}>Password</label>
                  <div className="relative group">
                    <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#F5F0E0]/20 group-focus-within:text-[#00FFD1] transition-colors" />
                    <Input type={showPassword ? 'text' : 'password'} placeholder="Enter password" value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="pl-10 pr-10 bg-[#1e1e1e] border-[#F5F0E0]/5 text-[#F5F0E0] placeholder:text-[#F5F0E0]/20 focus:border-[#00FFD1]/40 focus:ring-[#00FFD1]/10 rounded-xl h-11 transition-all"
                      required data-testid="password-input" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#F5F0E0]/20 hover:text-[#F5F0E0]/50 transition-colors"
                      data-testid="toggle-password-visibility">
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <Button type="submit" disabled={loading}
                  className="w-full bg-[#00FFD1] text-[#111] hover:bg-[#7DFFD4] font-bold py-5 rounded-xl group transition-all duration-300 hover:shadow-[0_0_30px_rgba(0,255,209,0.25)]"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                  data-testid="signin-btn">
                  {loading ? 'Signing in...' : 'Sign In'}
                  <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </form>
            </TabsContent>

            {/* Sign Up */}
            <TabsContent value="signup" className="mt-7">
              <form onSubmit={handleSignUp} className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-[#F5F0E0]/40 uppercase tracking-wider" style={{ fontFamily: "'Manrope', sans-serif" }}>Full Name</label>
                  <div className="relative group">
                    <User className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#F5F0E0]/20 group-focus-within:text-[#00FFD1] transition-colors" />
                    <Input type="text" placeholder="John Smith" value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="pl-10 bg-[#1e1e1e] border-[#F5F0E0]/5 text-[#F5F0E0] placeholder:text-[#F5F0E0]/20 focus:border-[#00FFD1]/40 focus:ring-[#00FFD1]/10 rounded-xl h-11 transition-all"
                      required data-testid="name-input" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-[#F5F0E0]/40 uppercase tracking-wider" style={{ fontFamily: "'Manrope', sans-serif" }}>Email</label>
                  <div className="relative group">
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#F5F0E0]/20 group-focus-within:text-[#00FFD1] transition-colors" />
                    <Input type="email" placeholder="you@company.com" value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="pl-10 bg-[#1e1e1e] border-[#F5F0E0]/5 text-[#F5F0E0] placeholder:text-[#F5F0E0]/20 focus:border-[#00FFD1]/40 focus:ring-[#00FFD1]/10 rounded-xl h-11 transition-all"
                      required />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-[#F5F0E0]/40 uppercase tracking-wider" style={{ fontFamily: "'Manrope', sans-serif" }}>Password</label>
                  <div className="relative group">
                    <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#F5F0E0]/20 group-focus-within:text-[#00FFD1] transition-colors" />
                    <Input type={showPassword ? 'text' : 'password'} placeholder="Create password" value={formData.password}
                      onChange={(e) => { setFormData({ ...formData, password: e.target.value }); if (passwordError) setPasswordError(''); }}
                      className="pl-10 pr-10 bg-[#1e1e1e] border-[#F5F0E0]/5 text-[#F5F0E0] placeholder:text-[#F5F0E0]/20 focus:border-[#00FFD1]/40 focus:ring-[#00FFD1]/10 rounded-xl h-11 transition-all"
                      required />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#F5F0E0]/20 hover:text-[#F5F0E0]/50 transition-colors"
                      data-testid="toggle-password-visibility-signup">
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-[#F5F0E0]/40 uppercase tracking-wider" style={{ fontFamily: "'Manrope', sans-serif" }}>Confirm Password</label>
                  <div className="relative group">
                    <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#F5F0E0]/20 group-focus-within:text-[#00FFD1] transition-colors" />
                    <Input type={showConfirmPassword ? 'text' : 'password'} placeholder="Confirm password" value={formData.confirmPassword}
                      onChange={(e) => { setFormData({ ...formData, confirmPassword: e.target.value }); if (passwordError) setPasswordError(''); }}
                      className={`pl-10 pr-10 bg-[#1e1e1e] border-[#F5F0E0]/5 text-[#F5F0E0] placeholder:text-[#F5F0E0]/20 focus:border-[#00FFD1]/40 focus:ring-[#00FFD1]/10 rounded-xl h-11 transition-all ${passwordError ? 'border-red-500/60 focus:border-red-500/60' : ''}`}
                      required />
                    <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#F5F0E0]/20 hover:text-[#F5F0E0]/50 transition-colors"
                      data-testid="toggle-confirm-password-visibility">
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {passwordError && <p className="text-sm text-red-400" style={{ fontFamily: "'Manrope', sans-serif" }}>{passwordError}</p>}
                </div>
                <Button type="submit" disabled={loading}
                  className="w-full bg-[#00FFD1] text-[#111] hover:bg-[#7DFFD4] font-bold py-5 rounded-xl group transition-all duration-300 hover:shadow-[0_0_30px_rgba(0,255,209,0.25)]"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                  data-testid="signup-btn">
                  {loading ? 'Creating account...' : 'Create Account & Continue'}
                  <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <Separator className="bg-[#F5F0E0]/5" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="px-3 bg-[#111] text-[#F5F0E0]/25" style={{ fontFamily: "'Manrope', sans-serif" }}>Or continue with</span>
            </div>
          </div>

          {/* Google */}
          <Button variant="outline"
            className="w-full py-5 bg-[#1e1e1e] border-[#F5F0E0]/5 text-[#F5F0E0]/70 hover:bg-[#2A2A2A] hover:border-[#00FFD1]/20 hover:text-[#F5F0E0] rounded-xl transition-all"
            onClick={handleGoogleLogin} data-testid="google-login-btn">
            <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <span style={{ fontFamily: "'Manrope', sans-serif" }}>Continue with Google</span>
          </Button>

          <p className="text-center text-xs text-[#F5F0E0]/20" style={{ fontFamily: "'Manrope', sans-serif" }}>
            By signing up, you'll be guided through setting up your AI receptionist.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
